
import React from 'react';
import { motion } from 'framer-motion';
import { Edit3, Cpu, Code2, CheckCircle } from 'lucide-react';

const HowItWorks = () => {
  const steps = [
    {
      icon: <Edit3 className="h-8 w-8 text-brand-purple" />,
      title: "Describe Your App",
      description: "Tell us what you want to build. The more details you provide, the better prompts we'll generate."
    },
    {
      icon: <Cpu className="h-8 w-8 text-brand-blue" />,
      title: "AI Generates Prompts",
      description: "Our AI analyzes your description and generates a series of specialized prompts optimized for your chosen IDE."
    },
    {
      icon: <Code2 className="h-8 w-8 text-brand-green" />,
      title: "Use in Your IDE",
      description: "Copy the generated prompts and paste them directly into Cursor, Windsurf, V0, or VS Code Copilot."
    },
    {
      icon: <CheckCircle className="h-8 w-8 text-yellow-500" />,
      title: "Build Faster",
      description: "Watch as your IDE creates exactly what you described, saving you hours of back-and-forth prompting."
    }
  ];

  return (
    <section className="py-20 relative overflow-hidden" id="how-it-works">
      {/* Background elements */}
      <div className="absolute inset-0 z-0">
        <div className="absolute top-40 -right-20 w-72 h-72 bg-brand-purple/20 rounded-full filter blur-3xl opacity-30"></div>
        <div className="absolute bottom-20 left-10 w-80 h-80 bg-brand-blue/20 rounded-full filter blur-3xl opacity-20"></div>
      </div>
      
      <div className="container mx-auto px-4 relative z-10">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-4 font-display">How It Works</h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            PromptAssist simplifies the process of creating effective AI prompts in just a few steps
          </p>
        </motion.div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {steps.map((step, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="glass p-6 text-center relative"
            >
              {index < steps.length - 1 && (
                <div className="hidden lg:block absolute top-1/2 -right-4 w-8 h-0.5 bg-gradient-to-r from-brand-purple to-brand-blue z-10"></div>
              )}
              
              <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center mx-auto mb-4">
                {step.icon}
              </div>
              
              <h3 className="text-xl font-semibold mb-2 font-display">{step.title}</h3>
              <p className="text-muted-foreground">{step.description}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;
